import type { TBaseResponse } from "@/models/common.model"

// Tipo de error de validación detallado por fila
export type TValidationError = {
  rowNumber: number
  errors: {
    cellRef: string
    fieldCode: string
    message: string
  }[]
}

// Resultado exitoso
type Success<T> = {
  data: T
  error: null
}

// Resultado fallido extendido (E extiende estructura base de error)
type Failure<E> = {
  data: null
  error: E
}

// Resultado genérico
type Result<T, E = { statusCode?: number, message: string }> = Success<T> | Failure<E>

// Función tryCatch que captura errores y los enriquece
export async function tryCatch<
  T,
  E extends { statusCode?: number, message: string, data?: T, validationErrors?: TValidationError[] } = {
    statusCode?: number
    message: string
    data?: T
    validationErrors?: TValidationError[]
  },
>(
  promise: Promise<TBaseResponse<T> | T>,
): Promise<Result<T, E>> {
  try {
    const response = await promise

    if (typeof response === "object" && response !== null && "value" in response) {
      const baseResponse = response as TBaseResponse<T>
      return { data: baseResponse.value, error: null }
    }

    return { data: response as T, error: null }
  }
  catch (error) {
    // Handle fetch errors
    if (error instanceof Error) {
      // Check if it's a fetch error
      if ("status" in error) {
        const fetchError = error as Error & { status?: number, data?: TBaseResponse<T> }
        return {
          data: null,
          error: {
            statusCode: fetchError.status,
            message: fetchError.data?.message || fetchError.message || "Fetch error",
            data: fetchError.data,
          } as unknown as E,
        }
      }

      // Handle other errors
      return {
        data: null,
        error: {
          statusCode: 500,
          message: error.message || "Unknown error",
          validationErrors: [],
        } as unknown as E,
      }
    }

    // Handle unknown errors
    return {
      data: null,
      error: {
        statusCode: 500,
        message: "Unknown error occurred",
        validationErrors: [],
      } as unknown as E,
    }
  }
}
